import { useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { Facility } from "../../../../api/facility";
import { hasValidationErrors } from "../../../../utils/utils";
import {
  createEmptyEnergyDraft,
  createEmptyHeatDraft,
  createEmptyRefrigerantDraft,
  createEmptyRegisterDraft,
  createEmptyWaterDraft,
  isEnergyRowBlank,
  isVolumeRowBlank,
  mapFacilityToDraft
} from "../utils/facilityDraft.utils";
import { useEnergyDistributionErrors } from "./useEnergyDistributionErrors";
import { FacilityDraftFormValues, PendingRecordDelete, SourceCollectionKey } from "../types/facilityDrawer.types";

interface UseFacilityDrawerFormParams {
  visibleModal: boolean;
  mode: "register" | "edit";
  initialFacility?: Partial<Facility>;
}

const buildInitialDraft = (mode: "register" | "edit", initialFacility?: Partial<Facility>) =>
  mode === "register" ? createEmptyRegisterDraft() : mapFacilityToDraft(initialFacility);

const emptyRecordFactories: Record<SourceCollectionKey, () => ReturnType<typeof createEmptyHeatDraft>> = {
  heatSource: createEmptyHeatDraft,
  waterSource: createEmptyWaterDraft,
  refrigerantSource: createEmptyRefrigerantDraft
};

export const useFacilityDrawerForm = ({ visibleModal, mode, initialFacility }: UseFacilityDrawerFormParams) => {
  const form = useForm<FacilityDraftFormValues>({
    defaultValues: buildInitialDraft(mode, initialFacility)
  });
  const { control, formState, reset } = form;

  const energyArray = useFieldArray({ control, name: "energy" });
  const heatArray = useFieldArray({ control, name: "heatSource" });
  const waterArray = useFieldArray({ control, name: "waterSource" });
  const refrigerantArray = useFieldArray({ control, name: "refrigerantSource" });

  // Each useFieldArray instance is generically tied to its own field name, so a Record keyed
  // by SourceCollectionKey pointing at them can't type-check structurally — switch instead.
  const sourceArrayFor = (collection: SourceCollectionKey) => {
    if (collection === "heatSource") return heatArray;
    if (collection === "waterSource") return waterArray;
    return refrigerantArray;
  };

  const wasVisibleRef = useRef(false);
  useEffect(() => {
    if (!visibleModal) {
      wasVisibleRef.current = false;
      return;
    }
    if (wasVisibleRef.current) {
      return;
    }
    wasVisibleRef.current = true;
    reset(buildInitialDraft(mode, initialFacility));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleModal]);

  const distributionErrors = useEnergyDistributionErrors(control);
  const saveDisabled = hasValidationErrors(formState.errors, distributionErrors);

  const [pendingDelete, setPendingDelete] = useState<PendingRecordDelete | null>(null);

  const requestDeleteRecord = (collection: PendingRecordDelete["collection"], index: number) => {
    setPendingDelete({ collection, index });
  };

  const cancelDeleteRecord = () => setPendingDelete(null);

  const confirmDeleteRecord = () => {
    if (!pendingDelete) return;
    if (pendingDelete.collection === "energy") {
      energyArray.remove(pendingDelete.index);
    } else {
      sourceArrayFor(pendingDelete.collection).remove(pendingDelete.index);
    }
    setPendingDelete(null);
  };

  const addSourceRecord = (collection: SourceCollectionKey) => {
    sourceArrayFor(collection).append(emptyRecordFactories[collection]());
  };

  const addEnergyRecord = () => {
    energyArray.append(createEmptyEnergyDraft());
  };


  const submitForm = (onValid: (values: FacilityDraftFormValues) => void) => () => {
    const values = form.getValues();

    const blankEnergyIndices = values.energy
      .map((row, index) => (isEnergyRowBlank(row) ? index : -1))
      .filter(index => index >= 0);
    if (blankEnergyIndices.length > 0) {
      energyArray.remove(blankEnergyIndices);
    }

    (["heatSource", "waterSource", "refrigerantSource"] as const).forEach(key => {
      const blankIndices = values[key]
        .map((row, index) => (isVolumeRowBlank(row) ? index : -1))
        .filter(index => index >= 0);
      if (blankIndices.length > 0) {
        sourceArrayFor(key).remove(blankIndices);
      }
    });

    return form.handleSubmit(onValid)();
  };

  return {
    form,
    control,
    formState,
    energyArray,
    heatArray,
    waterArray,
    refrigerantArray,
    distributionErrors,
    saveDisabled,
    pendingDelete,
    requestDeleteRecord,
    cancelDeleteRecord,
    confirmDeleteRecord,
    addSourceRecord,
    addEnergyRecord,
    submitForm
  };
};
